// Iter122ar · Componente condiviso: elenco moduli assegnati al condòmino con dialog di compilazione.
// Estratto da DocumentiCondominio.js per essere riutilizzato nel tab "Moduli" della Bacheca.
import { useEffect, useState } from "react";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { FileText, Printer, Send } from "lucide-react";

export function ModuliCondomino({ onCompilato }) {
  const [moduli, setModuli] = useState([]);
  const [compila, setCompila] = useState(null);
  const [risposte, setRisposte] = useState({});
  const [inviando, setInviando] = useState(false);
  const [fatto, setFatto] = useState(null);

  const load = () => {
    api.get("/condomino/moduli").then((r) => setModuli(r.data.moduli || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const apriModulo = (m) => { setCompila(m); setRisposte({}); setFatto(null); };

  const invia = async () => {
    setInviando(true);
    try {
      const { data } = await api.post(`/condomino/moduli/${compila.id}/compila`, { risposte });
      setFatto(data.compilazione_id);
      toast.success("Modulo inviato allo Studio");
      load();
      if (typeof onCompilato === "function") onCompilato();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setInviando(false); }
  };

  return (
    <Card className="p-4 border-2" data-testid="moduli-condomino-card">
      <h2 className="font-display text-xl font-bold text-brand mb-1">Moduli da compilare</h2>
      <p className="text-sm text-slate-500 mb-3">
        I moduli attivi predisposti dallo Studio. Compilando invii direttamente la richiesta e ricevi una copia PDF.
      </p>
      {moduli.length === 0 && (
        <div className="text-slate-500 text-sm" data-testid="moduli-vuoto">Nessun modulo disponibile al momento.</div>
      )}
      <div className="space-y-2">
        {moduli.map((m) => (
          <div key={m.id} className="p-3 rounded-lg border border-slate-200 flex flex-wrap items-start gap-2" data-testid={`modulo-cond-${m.id}`}>
            <FileText className="w-4 h-4 text-brand shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800 flex flex-wrap items-center gap-2 break-words">
                {m.titolo}
                {m.gia_compilato && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700"
                    data-testid={`modulo-gia-compilato-${m.id}`}>✓ già compilato</span>
                )}
              </div>
              {m.descrizione && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-line break-words">{m.descrizione}</div>}
            </div>
            <Button size="sm" className="h-8 px-3 text-xs bg-brand hover:bg-brand-dark shrink-0"
              onClick={() => apriModulo(m)} data-testid={`modulo-compila-${m.id}`}>
              {m.gia_compilato ? "Ri-compila" : "Compila"}
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!compila} onOpenChange={(o) => !o && setCompila(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl">{compila?.titolo}</DialogTitle></DialogHeader>
          {compila && !fatto && (
            <div className="space-y-3">
              {compila.descrizione && <p className="text-sm text-slate-600 bg-[#FBEEF3] border border-[#F0D9E1] rounded-lg p-3">{compila.descrizione}</p>}
              {compila.campi.map((c) => (
                <div key={c.id}>
                  <Label className="text-sm font-semibold">{c.etichetta}{c.obbligatorio && <span className="text-red-500 ml-0.5">*</span>}</Label>
                  {c.tipo === "testolungo" ? (
                    <textarea data-testid={`campo-${c.id}`} rows={3} className="w-full mt-1 border-2 border-slate-300 rounded-lg px-3 py-2 text-sm"
                      value={risposte[c.id] || ""} onChange={(e) => setRisposte({ ...risposte, [c.id]: e.target.value })} />
                  ) : c.tipo === "scelta" ? (
                    <select data-testid={`campo-${c.id}`} className="h-11 mt-1 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-sm"
                      value={risposte[c.id] || ""} onChange={(e) => setRisposte({ ...risposte, [c.id]: e.target.value })}>
                      <option value="">— seleziona —</option>
                      {(c.opzioni || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <Input data-testid={`campo-${c.id}`} type={c.tipo === "data" ? "date" : c.tipo === "numero" ? "number" : "text"}
                      className="h-11 mt-1" value={risposte[c.id] || ""} onChange={(e) => setRisposte({ ...risposte, [c.id]: e.target.value })} />
                  )}
                </div>
              ))}
            </div>
          )}
          {fatto && (
            <div className="space-y-3 text-center py-4" data-testid="modulo-inviato">
              <Send className="w-10 h-10 text-green-600 mx-auto" />
              <p className="font-semibold">Modulo inviato allo Studio!</p>
              <p className="text-sm text-slate-500">Puoi scaricare o stampare la tua copia in PDF.</p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" className="h-11" data-testid="modulo-pdf-download"
                  onClick={() => openSecureFile(`/moduli/compilazioni/${fatto}/pdf?inline=true`)}>
                  <Printer className="w-4 h-4 mr-2" /> Apri / stampa PDF
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            {!fatto ? (
              <>
                <Button variant="outline" onClick={() => setCompila(null)}>Annulla</Button>
                <Button data-testid="modulo-invia" onClick={invia} disabled={inviando} className="bg-brand hover:bg-brand-dark">
                  {inviando ? "Invio…" : "Invia allo Studio"}
                </Button>
              </>
            ) : (
              <Button onClick={() => setCompila(null)} className="bg-brand hover:bg-brand-dark">Chiudi</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
